import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cobrancasApi } from '@/api/endpoints/cobrancas';
import type {
  Cobranca,
  FiltrosCobranca,
  MarcarPagaInput,
  NovaCobrancaInput,
} from '@/api/endpoints/cobrancas';

export const useConfigPagamentos = () =>
  useQuery({
    queryKey: ['cobrancas-config'],
    queryFn: cobrancasApi.config,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const usePrecosDosPlanos = () =>
  useQuery({
    queryKey: ['cobrancas-precos'],
    queryFn: cobrancasApi.precos,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const useCobrancas = (filtros: FiltrosCobranca) =>
  useQuery({
    queryKey: ['cobrancas', filtros],
    queryFn: () => cobrancasApi.listar(filtros),
    staleTime: 1000 * 15,
    retry: 1,
  });

// Detalhe. Enquanto está pendente, busca de novo a cada 5 s: o webhook da
// AbacatePay marca como paga no servidor e a tela muda sozinha.
export const useCobranca = (id: number | null) =>
  useQuery({
    queryKey: ['cobranca', id],
    queryFn: () => cobrancasApi.obter(id as number),
    enabled: id != null,
    refetchInterval: (query) => (query.state.data?.status === 'PENDENTE' ? 5000 : false),
  });

export const useCriarCobranca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: NovaCobrancaInput) => cobrancasApi.criar(dados),
    onSuccess: (cobranca) => {
      qc.setQueryData(['cobranca', cobranca.id], cobranca);
      void qc.invalidateQueries({ queryKey: ['cobrancas'] });
    },
  });
};

export const useAtualizarCobranca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cobrancasApi.atualizar(id),
    onSuccess: (cobranca) => {
      qc.setQueryData(['cobranca', cobranca.id], cobranca);
      void qc.invalidateQueries({ queryKey: ['cobrancas'] });
    },
  });
};

export const useReembolsarCobranca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cobrancasApi.reembolsar(id),
    onSuccess: (cobranca) => {
      qc.setQueryData(['cobranca', cobranca.id], cobranca);
      void qc.invalidateQueries({ queryKey: ['cobrancas'] });
      void qc.invalidateQueries({ queryKey: ['finance'] });
    },
  });
};

// Baixa manual e estorno: mudam a assinatura do produtor e Finanças também.
const aposMudarPagamento = (qc: ReturnType<typeof useQueryClient>, cobranca: Cobranca) => {
  qc.setQueryData(['cobranca', cobranca.id], cobranca);
  void qc.invalidateQueries({ queryKey: ['cobrancas'] });
  void qc.invalidateQueries({ queryKey: ['finance'] });
  void qc.invalidateQueries({ queryKey: ['recorrencia'] });
  void qc.invalidateQueries({ queryKey: ['users'] });
};

export const useMarcarPaga = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dados }: { id: number; dados: MarcarPagaInput }) =>
      cobrancasApi.marcarPaga(id, dados),
    onSuccess: (cobranca) => aposMudarPagamento(qc, cobranca),
  });
};

export const useEstornarBaixa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, observacao }: { id: number; observacao: string }) =>
      cobrancasApi.estornarBaixa(id, observacao),
    onSuccess: (cobranca) => aposMudarPagamento(qc, cobranca),
  });
};
